import { ReactNode } from 'react';

import { Extension } from '@atlassian/forge-ui';
import { CoreData, RenderFn } from '@atlassian/forge-ui-types';
import { Iframe } from '@atlassian/forge-ui/iframe';

import { PullRequest, Repository, User, Workspace } from 'src/components/types';

export enum ForgeModuleType {
  // See https://hello.atlassian.net/wiki/spaces/BB/pages/2186198070/Extension+Points+Naming+Convention
  // for Bitbucket Forge module naming conventions
  REPO_PULL_REQUEST_CARD = 'bitbucket:repoPullRequestCard',
  REPO_PULL_REQUEST_ACTION = 'bitbucket:repoPullRequestAction',
  REPO_PULL_REQUEST_OVERVIEW_PANEL = 'bitbucket:repoPullRequestOverviewPanel',
  REPO_CODE_OVERVIEW_CARD = 'bitbucket:repoCodeOverviewCard',
  REPO_CODE_OVERVIEW_ACTION = 'bitbucket:repoCodeOverviewAction',
  REPO_MAIN_MENU_PAGE = 'bitbucket:repoMainMenuPage',
  REPO_SETTINGS_MENU_PAGE = 'bitbucket:repoSettingsMenuPage',
  WORKSPACE_SETTINGS_MENU_PAGE = 'bitbucket:workspaceSettingsMenuPage',
}

export enum ForgeUIType {
  UI_KIT_1 = 'ui-kit-1',
  UI_KIT_2 = 'ui-kit-2',
  CUSTOM_UI = 'custom-ui',
}

type WorkspaceContextSchema = {
  workspace: { uuid: string };
};

type RepositoryContextSchema = {
  repository: { uuid: string };
};

type PullRequestContextSchema = {
  pullRequest: { id: number };
};

type RepoPullRequestCardExtensionType = RepositoryContextSchema &
  PullRequestContextSchema;
type RepoPullRequestActionExtensionType = RepositoryContextSchema &
  PullRequestContextSchema;
type RepoCodeOverviewCardExtensionType = RepositoryContextSchema;
type RepoCodeOverviewActionExtensionType = RepositoryContextSchema;
type RepoMainMenuPageExtensionType = RepositoryContextSchema;
type RepoSettingsPageExtensionType = RepositoryContextSchema;
type WorkspaceSettingsPageExtensionType = WorkspaceContextSchema;
/**
 * Extracting out the BridgeConfig prop from the
 * Forge UI library which unfortunately the type
 * is not exported so using some Typescript helpers
 * to get access to that. openModal is omitted as
 * this is set within our ForgeIframeRenderer
 * component so no need to pass this down
 */
export type BridgeType = Omit<
  Required<React.ComponentProps<typeof Iframe>>['bridge'],
  'openModal'
>;

export type ForgeUIExtensionDataType = {
  type: ForgeModuleType;
} & (
  | RepoPullRequestCardExtensionType
  | RepoPullRequestActionExtensionType
  | RepoCodeOverviewCardExtensionType
  | RepoCodeOverviewActionExtensionType
  | RepoMainMenuPageExtensionType
  | RepoSettingsPageExtensionType
  | WorkspaceSettingsPageExtensionType
);

type ForgeAppPropsComponentType = (props: {
  app: Extension;
  children?: ReactNode;
  key?: string;
}) => JSX.Element;

export type ExtensionPointProps = {
  // if a POCO policy is set up for the extension point, a context token does not
  // need to be fetched here therefore shouldCallGetContextToken should be set to false
  shouldCallGetContextToken?: boolean;
  currentUser?: User;
  entryPoint?: string;
  onInitialRender?: () => void;
  onTearDown?: () => void;
  extensionData: ForgeUIExtensionDataType;
  workspace: Workspace;
  WrapperComponent: ForgeAppPropsComponentType;
  bridge?: BridgeType;
  extensionFilter?: (extension: Extension) => boolean;
};

export type ForgeExtensionPointAppsListType = {
  AppsListComponent: ForgeAppPropsComponentType;
  currentUser?: User;
  moduleType: ForgeModuleType;
  workspace: Workspace;
};

export type ForgeExtensionSingleAppProps = Omit<
  ExtensionPointProps,
  'WrapperComponent'
> & {
  extension: Extension;
};

export type ForgePageExtensionPointProps = {
  appId: string;
  extensionkey: string;
  envKey?: string;
};

export type ForgeFctWrapperProps = ExtensionPointProps & {
  shouldCallGetContextToken: boolean;
  extensions: ReadonlyArray<Extension>;
};

export type ForgeRepoMainMenuPageExtensionPointProps = Omit<
  ExtensionPointProps,
  'shouldCallGetContextToken' | 'extensionData'
> &
  ForgePageExtensionPointProps & { repository: Repository };

export type ForgeRepoSettingsMenuPageExtensionPointProps = Omit<
  ExtensionPointProps,
  'shouldCallGetContextToken' | 'extensionData'
> &
  ForgePageExtensionPointProps & { repository: Repository };

export type ForgeWorkspaceSettingsMenuPageExtensionPointProps = Omit<
  ExtensionPointProps,
  'shouldCallGetContextToken' | 'extensionData'
> &
  ForgePageExtensionPointProps;

export type ForgeRepoCodeOverviewCardExtensionPointProps = Omit<
  ExtensionPointProps,
  'shouldCallGetContextToken' | 'extensionData'
> & { repository: Repository };

export type ForgeRepoCodeOverviewActionExtensionPointAppsListProps = Omit<
  ForgeExtensionPointAppsListType,
  'moduleType'
>;

export type ForgeRepoCodeOverviewActionExtensionSingleAppProps = Omit<
  ForgeExtensionSingleAppProps,
  'shouldCallGetContextToken' | 'extensionData' | 'WrapperComponent'
> & { repository: Repository };

export type ForgeRepoPullRequestCardExtensionPointProps = Omit<
  ExtensionPointProps,
  'shouldCallGetContextToken' | 'extensionData'
> & { pullRequest: PullRequest; repository: Repository };

export type ForgeRepoPullRequestOverviewPanelExtensionPointProps = Omit<
  ExtensionPointProps,
  'shouldCallGetContextToken' | 'extensionData'
> & { pullRequest: PullRequest; repository: Repository };

export type ForgeRepoPullRequestActionExtensionPointAppsListProps = Omit<
  ForgeExtensionPointAppsListType,
  'moduleType'
>;

export type ForgeRepoPullRequestActionExtensionSingleAppProps = Omit<
  ForgeExtensionSingleAppProps,
  'shouldCallGetContextToken' | 'extensionData' | 'WrapperComponent'
> & { pullRequest: PullRequest; repository: Repository };

export type RendererProps = {
  app: Extension;
  bridge?: BridgeType;
  shouldCallGetContextToken: boolean;
  components?: {
    [props: string]: RenderFn;
  };
  contextIds: string[];
  currentUser?: User;
  entryPoint?: string;
  extensionData: ForgeUIExtensionDataType;
  locale?: string;
  workspaceUuid: string;
  getContextTokenFnGenerator: (extId: string) => () => Promise<string>;
  onInitialRender?: () => void;
  onTearDown?: () => void;
};

export type ForgeUIExtensionProps = Omit<
  RendererProps,
  'shouldCallGetContextToken' | 'getContextTokenFnGenerator' | 'workspaceUuid'
> & {
  getContextToken?: () => Promise<string>;
  coreData: CoreData;
};
